import { QUESTIONS_VALIDATION_SCHEMA } from "../Schemas/Questions";
import * as yup from "yup";
import safeRegex from "safe-regex";

export default class Rule {
    /**
     * @return {*}
     */
    static get SCHEMA_VALIDATION() {
        return yup
            .object()
            .shape({
                ruleId: yup.number().required(),
                questionCode: yup.string().required(),
                comparisonValue: yup.string().required(),
                comparison: yup
                    .string()
                    .oneOf(this.SUPPORTED_COMPARISONS)
                    .required()
            })
            .required();
    }

    /**
     * Returns an array of supported comparisons
     *
     * @return {[String]}
     * @constructor
     */
    static get SUPPORTED_COMPARISONS() {
        return [
            Rule.COMPARISON_GTE,
            Rule.COMPARISON_GT,
            Rule.COMPARISON_EQ,
            Rule.COMPARISON_LTE,
            Rule.COMPARISON_LT,
            Rule.COMPARISON_STARTS_WITH,
            Rule.COMPARISON_CONTAINS,
            Rule.COMPARISON_MATCHES
        ];
    }

    /**
     * The "Greater than or equal to" comparison
     *
     * @return {String}
     * @constructor
     */
    static get COMPARISON_GTE() {
        return ">=";
    }

    /**
     * The "Greater than" comparison
     *
     * @return {String}
     * @constructor
     */
    static get COMPARISON_GT() {
        return ">";
    }

    /**
     * The "Equal to" comparison (case-insensitive string match)
     *
     * @return {String}
     * @constructor
     */
    static get COMPARISON_EQ() {
        return "=";
    }

    /**
     * The "Less than or equal to" comparison
     *
     * @return {String}
     * @constructor
     */
    static get COMPARISON_LTE() {
        return "<=";
    }

    /**
     * The "Less than" comparison
     *
     * @return {String}
     * @constructor
     */
    static get COMPARISON_LT() {
        return "<";
    }

    /**
     * The "Starts with" comparison (case-insensitive string prefix)
     *
     * @return {String}
     * @constructor
     */
    static get COMPARISON_STARTS_WITH() {
        return "startsWith";
    }

    /**
     * The "Contains" comparison (case-insensitive substring)
     *
     * @return {String}
     * @constructor
     */
    static get COMPARISON_CONTAINS() {
        return "contains";
    }

    /**
     * The "Matches" comparison (comparisonValue is a regular expression, case-insensitive)
     *
     * @return {String}
     * @constructor
     */
    static get COMPARISON_MATCHES() {
        return "matches";
    }

    /**
     * Lower-cases a string for case-insensitive comparisons; non-strings are returned unchanged
     *
     * @param {*} value
     * @return {*}
     */
    static lower(value) {
        return typeof value === "string" ? value.toLowerCase() : value;
    }

    /**
     * Whether a regex pattern avoids backreferences (\1-\9, \k<name>), which aren't supported
     *
     * @param {String} pattern
     * @return {Boolean}
     */
    static hasNoBackreferences(pattern) {
        for (let i = 0; i < pattern.length; i++) {
            if (pattern[i] === "\\") {
                if (/[1-9k]/.test(pattern[i + 1] || "")) return false;
                i++;
            }
        }
        return true;
    }

    /**
     * Tests a value against a regular expression pattern, treating an invalid pattern, a backreference
     * or one that could backtrack catastrophically (e.g. nested quantifiers like "(a+)+") as no match
     *
     * @param {String} pattern
     * @param {*} value
     * @return {Boolean}
     */
    static regexTest(pattern, value) {
        if (!Rule.hasNoBackreferences(pattern) || !safeRegex(pattern)) return false;

        try {
            return new RegExp(pattern, "i").test(String(value));
        } catch (e) {
            return false;
        }
    }

    /**
     * Initialises a Rule with specific schema
     * @param {{ruleId: Number, questionCode:String, comparisonValue:String, comparison:String}} schema
     */
    constructor(schema) {
        Rule.SCHEMA_VALIDATION.validateSync(schema);

        this._ruleId = schema.ruleId;
        this._questionCode = schema.questionCode;
        this._comparisonValue = schema.comparisonValue;
        this._comparison = schema.comparison;
    }

    /**
     * @return {{comparison: String, comparisonValue: String, questionCode: String}}
     */
    get schema() {
        return {
            ruleId: this._ruleId,
            questionCode: this._questionCode,
            comparisonValue: this._comparisonValue,
            comparison: this._comparison
        };
    }

    /**
     * Validates the current rule against an array of answers
     *
     * @param {[{questionCode:String, answers:[{answer:String}]}]} questions
     * @return {{valid:Boolean, schema:Object, match:String}}
     * @throws Error
     */
    validate(questions) {
        QUESTIONS_VALIDATION_SCHEMA.validateSync(questions);

        let valid = false;
        let match = null;
        for (let question of questions) {
            if (question.questionCode === this._questionCode) {
                for (let answer of question.answers) {
                    const answerValue = answer.answer;

                    // validate using the configured comparison
                    switch (this._comparison) {
                        case Rule.COMPARISON_GTE:
                            valid = Number(answerValue) >= Number(this._comparisonValue);
                            break;
                        case Rule.COMPARISON_GT:
                            valid = Number(answerValue) > Number(this._comparisonValue);
                            break;
                        case Rule.COMPARISON_EQ:
                            valid = Rule.lower(answerValue) === Rule.lower(this._comparisonValue);
                            break;
                        case Rule.COMPARISON_LTE:
                            valid = Number(answerValue) <= Number(this._comparisonValue);
                            break;
                        case Rule.COMPARISON_LT:
                            valid = Number(answerValue) < Number(this._comparisonValue);
                            break;
                        case Rule.COMPARISON_STARTS_WITH:
                            valid = Rule.lower(String(answerValue)).startsWith(Rule.lower(this._comparisonValue));
                            break;
                        case Rule.COMPARISON_CONTAINS:
                            valid = Rule.lower(String(answerValue)).includes(Rule.lower(this._comparisonValue));
                            break;
                        case Rule.COMPARISON_MATCHES:
                            valid = Rule.regexTest(this._comparisonValue, answerValue);
                            break;
                    }

                    // store the match to report to consumer
                    if (valid) {
                        match = answerValue;
                        break;
                    }
                }

                break;
            }
        }

        return {
            valid,
            schema: this.schema,
            match
        };
    }
}
